import { TRPCClientError } from '@trpc/client';
import { CopyCheckIcon, CopyIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import modalStore, { ModalOption } from '@client/store/modalStore';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';
import { Modal } from './Modal';
import { createPlugin } from './modalPlugin';

// ---------------------------------------------------------------------------
// AlertModal
// ---------------------------------------------------------------------------

const AlertModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <p>{option.alert?.message}</p>
            </Modal.BodyScroll>
            <Modal.ActionButton
                modalId={modalId}
                confirmStyle="default"
                isShowCancel={false}
                confirmText={option.alert?.confirmText}
            />
        </Modal.Dialog>
    );
});

/**
 * alert 모달 플러그인
 * - 단순 메시지 확인 모달
 */
export const alertPlugin = createPlugin({
    type: 'alert' as const,
    component: AlertModal,
    createModalOption: ({
        message,
        confirmText = '확인',
    }: {
        message: string;
        confirmText?: string;
    }) => ({
        backdrop: true,
        preventClose: false,
        alert: {
            message,
            confirmText,
        },
    }),
});

// ---------------------------------------------------------------------------
// ConfirmModal
// ---------------------------------------------------------------------------

const ConfirmModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const [isChecked, setIsChecked] = useState(false);

    const hasCheckbox = !!option.confirm?.checkbox;
    const isCheckboxRequired = option.confirm?.checkbox?.required ?? false;
    const canConfirm = !hasCheckbox || !isCheckboxRequired || isChecked;
    const confirmStyle = option.confirm?.confirmStyle ?? 'default';

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <p>{option.confirm?.message}</p>
                {hasCheckbox && (
                    <div className="form-control mt-8">
                        <label className="label cursor-pointer justify-start gap-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={isChecked}
                                onChange={(e) => setIsChecked(e.target.checked)}
                            />
                            <span className="label-text text-base-content text-base">
                                {option.confirm?.checkbox?.text}
                            </span>
                        </label>
                    </div>
                )}
            </Modal.BodyScroll>
            <Modal.ActionButton
                modalId={modalId}
                confirmStyle={confirmStyle}
                isShowCancel={true}
                canConfirm={canConfirm}
                cancelText={option.confirm?.cancelText}
                confirmText={option.confirm?.confirmText}
            />
        </Modal.Dialog>
    );
});

/**
 * confirm 모달 플러그인
 * - 확인/취소 버튼이 있는 확인 모달
 */
export const confirmPlugin = createPlugin({
    type: 'confirm' as const,
    component: ConfirmModal,
    createModalOption: ({
        title,
        message,
        confirmText = '확인',
        cancelText = '취소',
        preventClose = true,
        checkbox,
        confirmStyle = 'default',
    }: {
        title?: string;
        message: string;
        confirmText?: string;
        cancelText?: string;
        preventClose?: boolean;
        checkbox?: {
            text: string;
            required?: boolean;
        };
        confirmStyle?: 'default' | 'danger';
    }) => ({
        title,
        backdrop: true,
        preventClose,
        confirm: {
            message,
            confirmText,
            cancelText,
            checkbox,
            confirmStyle,
        },
    }),
});

// ---------------------------------------------------------------------------
// CustomModal
// ---------------------------------------------------------------------------

const CustomModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const { custom } = option;

    if (!custom) {
        return null;
    }

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <div>{custom.renderComponent({ modalId })}</div>
            </Modal.BodyScroll>
        </Modal.Dialog>
    );
});

/**
 * custom 모달 플러그인
 * - 임의의 컴포넌트를 렌더링하는 모달
 */
export const customPlugin = createPlugin({
    type: 'custom' as const,
    component: CustomModal,
    createModalOption: ({
        title,
        renderComponent,
        onOpen,
        showCloseButton = true,
        preventClose = false,
        maxWidth,
    }: {
        title?: string;
        renderComponent: ({ modalId }: { modalId: string }) => React.ReactNode;
        onOpen?: ({ modalId }: { modalId: string }) => void;
        showCloseButton?: boolean;
        preventClose?: boolean;
        maxWidth?: string;
    }) => ({
        title,
        onOpen,
        backdrop: true,
        preventClose,
        showCloseButton,
        maxWidth,
        custom: {
            renderComponent,
        },
    }),
});

// ---------------------------------------------------------------------------
// SelectModal
// ---------------------------------------------------------------------------

const SelectModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const selectOption = option.select!;
    const [selectedValue, setSelectedValue] = useState(selectOption.selectedValue);

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <div className="flex flex-col gap-2">
                    {selectOption.items.map((item, index) => (
                        <label
                            key={index}
                            className={twMerge('flex cursor-pointer items-center gap-3 py-2')}
                            onClick={() => {
                                const value = item.value;

                                setSelectedValue(value);

                                if (selectOption.closeOnSelect) {
                                    modalStore.setReturnValue(modalId, value);
                                }
                            }}
                        >
                            {item.icon && (
                                <div
                                    className={twMerge(
                                        'text-base-content/60 flex-shrink-0',
                                        selectedValue === item.value && 'text-base-content',
                                    )}
                                >
                                    {item.icon}
                                </div>
                            )}
                            <div className="flex-1">
                                <div
                                    className={twMerge(
                                        'text-base-content transition-all',
                                        selectedValue === item.value
                                            ? 'font-semibold'
                                            : 'font-normal',
                                    )}
                                >
                                    {item.name}
                                </div>
                            </div>
                            {selectOption.showRadio && (
                                <input
                                    type="radio"
                                    name={`select_${modalId}`}
                                    checked={selectedValue === item.value}
                                    onChange={() => {}}
                                    className="radio radio-sm flex-shrink-0"
                                />
                            )}
                        </label>
                    ))}
                </div>
            </Modal.BodyScroll>
            {!selectOption.closeOnSelect && (
                <Modal.ActionButton
                    className="mt-6"
                    modalId={modalId}
                    confirmStyle="default"
                    isShowCancel={false}
                    confirmText={selectOption.confirmText || '선택'}
                    onConfirm={() => {
                        modalStore.setReturnValue(modalId, selectedValue);
                    }}
                />
            )}
        </Modal.Dialog>
    );
});

/**
 * select 모달 플러그인
 * - 목록에서 항목을 선택하는 모달
 */
export const selectPlugin = createPlugin({
    type: 'select' as const,
    component: SelectModal,
    createModalOption: ({
        title,
        selectedValue,
        showRadio = true,
        closeOnSelect = true,
        confirmText,
        preventClose = false,
        items,
    }: {
        title?: string;
        selectedValue?: unknown;
        showRadio?: boolean;
        closeOnSelect?: boolean;
        confirmText?: string;
        preventClose?: boolean;
        items: {
            name: string;
            value: unknown;
            icon?: React.ReactNode;
        }[];
    }) => ({
        title,
        backdrop: true,
        preventClose,
        select: {
            selectedValue,
            showRadio,
            closeOnSelect,
            confirmText,
            preventClose,
            items,
        },
    }),
});

// ---------------------------------------------------------------------------
// MenuModal
// ---------------------------------------------------------------------------

const MenuModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const menuOption = option.menu!;

    const handleItemClick = async (item: (typeof menuOption.items)[0]) => {
        try {
            await item.onSelect({ modalId });

            if (menuOption.closeOnSelect) {
                modalStore.setReturnValue(modalId, undefined);
            }
        } catch (error) {
            console.error('Menu item action failed:', error);
        }
    };

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <div className="flex flex-col gap-2">
                    {menuOption.items.map((item, index) => (
                        <button
                            key={index}
                            className={twMerge('flex cursor-pointer items-center gap-3 py-2')}
                            onClick={(e) => {
                                e.preventDefault();
                                handleItemClick(item);
                            }}
                        >
                            {item.icon && (
                                <div className="text-base-content shrink-0">{item.icon}</div>
                            )}
                            <div className="grow text-left font-normal">{item.name}</div>
                        </button>
                    ))}
                </div>
            </Modal.BodyScroll>
        </Modal.Dialog>
    );
});

/**
 * menu 모달 플러그인
 * - 메뉴 항목 목록을 표시하는 모달
 */
export const menuPlugin = createPlugin({
    type: 'menu' as const,
    component: MenuModal,
    createModalOption: ({
        title,
        closeOnSelect = true,
        preventClose = false,
        items,
    }: {
        title?: string;
        closeOnSelect?: boolean;
        preventClose?: boolean;
        items: {
            name: string;
            icon?: React.ReactNode;
            onSelect: ({ modalId }: { modalId: string }) => void | Promise<void>;
        }[];
    }) => ({
        title,
        backdrop: true,
        preventClose,
        menu: {
            closeOnSelect,
            preventClose,
            items,
        },
    }),
});

// ---------------------------------------------------------------------------
// ErrorModal
// ---------------------------------------------------------------------------

class ErrorViewStore {
    isDetailOpen = false;
    isCopied = false;

    constructor() {
        makeAutoObservable(this);
    }

    toggleDetail() {
        this.isDetailOpen = !this.isDetailOpen;
    }

    async copyDetails(code: string, path: string) {
        let text = '';
        if (code) {
            text += `코드: ${code}\n`;
        }
        if (path) {
            text += `경로: ${path}\n`;
        }

        await navigator.clipboard.writeText(text);

        runInAction(() => {
            this.isCopied = true;
        });
    }
}

const ErrorModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const viewStore = useMobxInstance(ErrorViewStore);
    const { navigate } = useCommonNavigate();
    const { error, message: messageOption, linkTo } = option.error!;

    let message = messageOption || '';
    let path = '';
    let stack = '';
    let code = '';

    if (error instanceof TRPCClientError) {
        path = error.data?.path;
        stack = error.data?.stack;
        message = error.data?.cause?.message || error.message || message;
        code = error.data?.code;
    } else if (error instanceof Error) {
        message = error.message || message;
    } else {
        message = String(error) || message;
    }

    if (messageOption) {
        message = messageOption;
    }

    if (!message) {
        message = '오류가 발생했습니다. 다시 시도해주세요.';
    }

    const hasDetails = path || code;

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <p>{message}</p>
                {hasDetails && (
                    <div className="mt-4">
                        <button
                            type="button"
                            className="text-base-content/60 flex cursor-pointer items-center gap-1 text-xs"
                            onClick={() => {
                                viewStore.toggleDetail();
                            }}
                        >
                            자세히 보기
                        </button>
                        {viewStore.isDetailOpen && (
                            <div className="flex flex-row items-center justify-between">
                                <div className="text-base-content/80 mt-2 space-y-1 text-sm">
                                    {code && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-base-content/70 text-xs">
                                                코드:
                                            </span>{' '}
                                            {code}
                                        </div>
                                    )}
                                    {path && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-base-content/70 text-xs">
                                                경로:
                                            </span>{' '}
                                            {path}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0">
                                    <button
                                        className={twMerge('btn btn-circle btn-ghost btn-sm')}
                                        onClick={async () => {
                                            await viewStore.copyDetails(code, path);
                                        }}
                                    >
                                        {viewStore.isCopied ? (
                                            <CopyCheckIcon
                                                className="size-4"
                                                color="var(--color-primary)"
                                            />
                                        ) : (
                                            <CopyIcon className="size-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {import.meta.env.DEV && stack && (
                    <pre className="bg-base-200 mt-4 w-full overflow-auto rounded-md p-2">
                        <code className="text-base-content/65 text-xs">{stack}</code>
                    </pre>
                )}
            </Modal.BodyScroll>
            <Modal.ActionButton
                modalId={modalId}
                confirmStyle="default"
                isShowCancel={true}
                cancelText="닫기"
                confirmText="홈으로 이동"
                onConfirm={() => {
                    navigate(linkTo || '/');
                    modalStore.setReturnValue(modalId, true);
                }}
            />
        </Modal.Dialog>
    );
});

/**
 * error 모달 플러그인
 * - 에러 메시지를 표시하는 모달
 */
export const errorPlugin = createPlugin({
    type: 'error' as const,
    component: ErrorModal,
    createModalOption: ({
        error,
        title,
        message,
        linkTo,
    }: {
        error?: Error | unknown;
        title?: string;
        message?: string;
        linkTo?: string;
    }) => ({
        title,
        backdrop: true,
        preventClose: true,
        error: {
            error,
            message,
            linkTo,
        },
    }),
});

// ---------------------------------------------------------------------------
// commonPlugins
// ---------------------------------------------------------------------------

/**
 * 공통 플러그인 모음
 * - alert, confirm, custom, select, menu, error
 * - login은 앱별로 다르게 구현하므로 포함하지 않음
 */
export const commonPlugins = {
    alert: alertPlugin,
    confirm: confirmPlugin,
    custom: customPlugin,
    select: selectPlugin,
    menu: menuPlugin,
    error: errorPlugin,
} as const;
